import { Link } from "@tanstack/react-router";
import { Instagram, Facebook, Youtube } from "lucide-react";
import { XLogo } from "@/components/site/icons/XLogo";

import { LanguageSwitcher } from "@/components/site/LanguageSwitcher";

// Vertical padding exists only below the sm breakpoint, so phone tappable
// areas reach ~40px while tablet/PC rendering is unchanged.
const linkClass = "inline-block py-2.5 sm:py-0 text-brand-deep hover:underline";
const headingClass = "text-sm font-bold uppercase tracking-wide text-brand-deep";

export function Footer() {
  return (
    <footer className="border-t border-border bg-background flex items-center">
      <div className="mx-auto w-full max-w-7xl px-6 py-6 flex flex-col gap-6">
        <p className="text-center text-xs text-muted-foreground">
          Serving Northern Virginia and the Washington, D.C. metro area — including Herndon, Reston, Fairfax, Loudoun, Arlington, Alexandria, and surrounding communities.
        </p>

        {/* Site-wide crawlable links to every procedure, revision, and
            medication page, so each gets a footer link from every page. */}
        <nav aria-label="Footer" className="grid grid-cols-1 gap-6 text-sm sm:grid-cols-3">
          <div className="flex flex-col items-start gap-2">
            <h2 className={headingClass}>Bariatric Procedures</h2>
            <Link to="/services/sleeve" className={linkClass}>Sleeve Gastrectomy</Link>
            <Link to="/services/bypass" className={linkClass}>Gastric Bypass</Link>
            <Link to="/services/band" className={linkClass}>Adjustable Gastric Banding</Link>
            <Link to="/services/balloon" className={linkClass}>Intragastric Balloon</Link>
            <Link to="/services/esg" className={linkClass}>Endoscopic Sleeve Gastroplasty (ESG)</Link>
            <Link to="/services/bypass-vs-sleeve" className={linkClass}>Gastric Bypass vs. Sleeve</Link>
            <Link to="/self-pay-bariatric-surgery" className={linkClass}>Self-Pay Pricing</Link>
            <Link to="/resources/insurance-coverage" className={linkClass}>Insurance Coverage for Surgery</Link>
            <Link to="/resources/prosper" className={linkClass}>Financing &amp; Payment Plans</Link>
          </div>

          <div className="flex flex-col items-start gap-2">
            <h2 className={headingClass}>Revisions &amp; Medications</h2>
            <Link to="/services/revisions/sleeve" className={linkClass}>Gastric Sleeve Revision</Link>
            <Link to="/services/revisions/gastric-bypass" className={linkClass}>Gastric Bypass Revision</Link>
            <Link to="/services/revisions/lap-band" className={linkClass}>LAP-BAND Removal and Revision Surgery</Link>
            <Link to="/services/revisions/tore" className={linkClass}>TORe</Link>
            <Link to="/services/hiatal-hernia-surgery" className={linkClass}>Hiatal Hernia Repair</Link>
            <Link to="/medical-weight-loss/semaglutide" className={linkClass}>Semaglutide (Wegovy® &amp; Ozempic®)</Link>
            <Link to="/medical-weight-loss/tirzepatide" className={linkClass}>Tirzepatide (Zepbound® &amp; Mounjaro®)</Link>
            <Link to="/medical-weight-loss/oral-medications" className={linkClass}>Oral Medications</Link>
            <Link to="/medical-weight-loss/insurance" className={linkClass}>Insurance Coverage for Medication</Link>
          </div>

          <div className="flex flex-col items-start gap-2">
            <h2 className={headingClass}>Explore</h2>
            <Link to="/" className={linkClass}>Home</Link>
            <Link to="/about" className={linkClass}>About Dr. Fitzer</Link>
            <Link to="/team" className={linkClass}>Our Team</Link>
            <Link to="/results" className={linkClass}>Before &amp; After Results</Link>
            <Link to="/resources" className={linkClass}>Patient Links</Link>
            <Link to="/resources/obesity-and-diabetes" className={linkClass}>Obesity and Type-2 Diabetes</Link>
            <Link to="/dr-fitzer-dissects-the-news" className={linkClass}>Dr. Fitzer Dissects the News</Link>
            <Link to="/contact" className={linkClass}>Contact</Link>
            {/* Internal link so the app privacy policy isn't an orphaned page in the sitemap. */}
            <Link to="/app-privacy-policy" className={linkClass}>App &amp; Website Privacy Policy</Link>
            
          </div>
        </nav>

        <p className="text-center text-sm font-bold">
          <a href="tel:7037099771" aria-label="Call Virginia Bariatric Surgery at 703-709-9771" className={`inline-block py-2.5 sm:py-0 whitespace-nowrap font-bold ${linkClass}`}>(703) 709-9771</a>
        </p>

        {/* Spanish footer links: a crawlable path to /es and /es/contacto from
            every page on the site, so Google discovers them without redirects. */}
        <p className="text-center text-sm text-muted-foreground" lang="es">
          <span className="mr-2">En español:</span>
          <Link to="/es" hrefLang="es" lang="es" className={linkClass}>Inicio</Link>
          <span className="mx-2" aria-hidden="true">·</span>
          <Link to="/es/contacto" hrefLang="es" lang="es" className={linkClass}>Contacto</Link>
        </p>
        {/* Same switcher as the header: keeps the visitor on the current page
            and remembers the language choice for future visits. */}
        <p className="text-center text-sm text-muted-foreground">
          <LanguageSwitcher className={linkClass} />
        </p>
        <div className="flex items-center justify-center gap-4 pt-1">
          <a
            href="https://www.instagram.com/doctorfitzer/"
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Follow Dr. Matthew Fitzer on Instagram"
            className="inline-flex min-h-11 min-w-11 items-center justify-center rounded-full p-2 text-brand-deep hover:bg-brand-soft/40 transition-colors"
          >
            <Instagram size={20} aria-hidden="true" />
          </a>
          <a
            href="https://www.facebook.com/virginiabariatric/"
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Visit Virginia Bariatric Surgery on Facebook"
            className="inline-flex min-h-11 min-w-11 items-center justify-center rounded-full p-2 text-brand-deep hover:bg-brand-soft/40 transition-colors"
          >
            <Facebook size={20} aria-hidden="true" />
          </a>
          <a
            href="https://www.youtube.com/@virginiabariatricsurgeryce114"
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Visit Virginia Bariatric Surgery on YouTube"
            className="inline-flex min-h-11 min-w-11 items-center justify-center rounded-full p-2 text-brand-deep hover:bg-brand-soft/40 transition-colors"
          >
            <Youtube size={20} aria-hidden="true" />
          </a>
          <a
            href="https://x.com/vbariatric"
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Follow Virginia Bariatric on X (Twitter)"
            className="inline-flex min-h-11 min-w-11 items-center justify-center rounded-full p-2 text-brand-deep hover:bg-brand-soft/40 transition-colors"
          >
            <XLogo size={18} />
          </a>
        </div>
        <img
          src="/logo.svg"
          alt="Virginia Bariatric Surgery logo"
          width={1500}
          height={321}
          loading="lazy"
          decoding="async"
          className="mx-auto mt-2 h-5 w-auto sm:h-6 dark:brightness-[1.55]"
        />
        <p className="text-center text-xs text-muted-foreground pt-2">
          © 2026 Virginia Bariatric Surgery, P.C. All rights reserved.
        </p>
      </div>
    </footer>
  );
}
